class ShippingProtection extends HTMLElement {
	constructor() {
		super();
		this.itemName = `shippingProtection_${Shopify.shop}`;
		this.insuranceProduct = JSON.parse(
			this.querySelector('[type="application/json"]').textContent
		);

		this.cartUpdated = this.onCartUpdate.bind(this);

		this.insuranceId = this.insuranceProduct.id;
		this.insuranceVariantId = this.insuranceProduct.variants[0].id;
		this.itemSelector = `.cart-item[data-product-id="${this.insuranceId}"]`;

		this.cart =
			document.querySelector("cart-notification") ||
			document.querySelector("cart-drawer");

		this.checkbox = this.querySelector('input[type="checkbox"]');
		if (this.checkbox) {
			this.checkbox.addEventListener(
				"change",
				this.checkboxHandler.bind(this)
			);
		}
	}

	connectedCallback() {
		const itemValue = localStorage.getItem(this.itemName) || "true";
		this.updateState(itemValue === "true");
		document.addEventListener("cart:change", this.cartUpdated);
		// The drawer replaces its children on the first add, which disconnects
		// this element before its cart:change listener can run. Re-check the
		// cart whenever a new element connects so protection still defaults on.
		if (itemValue === "true") this.ensureInsurance();
	}

	async ensureInsurance() {
		if (this._ensuring) return;
		this._ensuring = true;
		try {
			const cart = await (
				await fetch(`${Shopify.routes.root}cart.js`, { cache: "no-store" })
			).json();
			if (!this.isConnected) return;
			const hasInsurance = cart.items.some(
				(item) => item.product_id === this.insuranceId
			);
			const merchandiseCount = cart.items.reduce(
				(sum, item) =>
					item.product_id === this.insuranceId ? sum : sum + item.quantity,
				0
			);
			if (merchandiseCount > 0 && !hasInsurance) {
				await this.addInsurance(false);
			} else if (merchandiseCount === 0 && hasInsurance) {
				await this.removeInsurance(false);
			}
		} catch (error) {
			console.error(error);
		} finally {
			this._ensuring = false;
		}
	}

	disconnectedCallback() {
		document.removeEventListener("cart:change", this.cartUpdated);
	}

	updateItem(id, quantity, { render = true } = {}) {
		return fetch(`${Shopify.routes.root}cart/change.js`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				id: `${id}`,
				quantity,
			}),
		}).then(async (response) => {
			const payload = await response.json().catch(() => ({}));
			if (!response.ok || payload.status) {
				const error = new Error(
					payload.description || payload.message || "Could not update cart"
				);
				error.payload = payload;
				throw error;
			}
			if (render) this.renderCart();
			return payload;
		});
	}

	addItemById(id, quantity, { render = true } = {}) {
		return fetch(`${Shopify.routes.root}cart/add.js`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Accept: `application/javascript`,
				"X-Requested-With": "XMLHttpRequest",
			},
			body: JSON.stringify({
				items: [
					{
						id,
						quantity,
					},
				],
			}),
		})
			.then(async (response) => {
				const payload = await response.json().catch(() => ({}));
				if (!response.ok || payload.status) {
					const error = new Error(
						payload.description || payload.message || "Could not add protection"
					);
					error.payload = payload;
					throw error;
				}
				if (!this.cart) {
					window.location = window.routes.cart_url;
					return payload;
				}
				if (render) this.renderCart();
				return payload;
			});
	}

	async renderCart() {
		document.documentElement.dispatchEvent(
			new CustomEvent("cart:refresh", {
				bubbles: true,
				detail: {},
			})
		);
	}

	onCartUpdate(event) {
		const isEnabled = localStorage.getItem(this.itemName) === "true";
		if (!isEnabled) return;

		const itemCount = event.detail.cart["item_count"];
		const hasInsurance = event.detail.cart.items.some(
			(item) => item.product_id === this.insuranceId
		);
		if (itemCount >= 1 && !hasInsurance) {
			this.addInsurance(false).catch((error) => console.error(error));
		} else if (itemCount === 1 && hasInsurance) {
			this.removeInsurance(false).catch((error) => console.error(error));
		}
	}

	checkboxHandler() {
		const action = this.checkbox.checked
			? this.addInsurance()
			: this.removeInsurance();
		action.catch((error) => console.error(error));
	}

	updateState(state) {
		if (this.checkbox) this.checkbox.checked = state;
		this.enabled = state;
		localStorage.setItem(this.itemName, state);
	}

	addInsurance(updateState = true, options = {}) {
		if (updateState) {
			this.updateState(true);
		}
		return this.addItemById(this.insuranceVariantId, 1, options);
	}

	removeInsurance(updateState = true, options = {}) {
		if (updateState) {
			this.updateState(false);
		}
		return this.updateItem(this.insuranceVariantId, 0, options);
	}

	setInsurancePrice() {
		const currentInsurance =
			document.querySelector(this.itemSelector)?.dataset.productPrice ||
			0;
		const cartTotal =
			document.querySelector("[data-cart-total]").dataset.cartTotal;
		const primceAim =
			(cartTotal - currentInsurance) * (this.dataset.percentage / 100);

		this.querySelector("span[data-price]").textContent = Math.round(
			primceAim / 100
		);
	}
}
customElements.define("shipping-protection", ShippingProtection);

class RemoveInsurance extends HTMLElement {
	constructor() {
		super();
		this.addEventListener("click", this.removeInsurance.bind(this));
	}

	removeInsurance(e) {
		e.preventDefault();
		document.querySelector("shipping-protection")?.removeInsurance()?.catch((error) => {
			console.error(error);
		});
	}
}

customElements.define("remove-insurance", RemoveInsurance);

// Preference key: shippingProtection_<shop>. Values are "true" or "false".
// A missing key means protection stays on.
// "Checkout without shipping protection" sets the key to false, removes the
// line, and only then opens checkout. Clearing the cart does not clear the
// key, so the next add-to-cart stays unprotected until "Add shipping protection".
let protectionChoiceLocked = false;

function protectionVariantStillInCart(cart, variantId) {
	if (!cart || !Array.isArray(cart.items)) return false;
	return cart.items.some(
		(item) => Number(item.variant_id || item.id) === Number(variantId)
	);
}

document.addEventListener("click", (event) => {
	const button = event.target.closest("[data-protection-choice]");
	if (!button || button.disabled || protectionChoiceLocked) return;

	const element = document.querySelector("shipping-protection");
	if (!element) return;

	event.preventDefault();
	const choice = button.dataset.protectionChoice;
	const form = button.closest("form");
	const buttons = form ? [...form.querySelectorAll("button, a.button")] : [button];
	const label = button.textContent;
	const errorEl = form?.querySelector("[data-protection-error]");
	const previous = localStorage.getItem(element.itemName);
	const wasOn = previous === null || previous === "true";

	protectionChoiceLocked = true;
	buttons.forEach((el) => {
		el.disabled = true;
	});
	button.setAttribute("aria-busy", "true");
	button.textContent = button.dataset.pendingLabel || "Updating cart…";
	if (errorEl) errorEl.hidden = true;

	const fail = (error) => {
		console.error(error);
		element.updateState(wasOn);
		protectionChoiceLocked = false;
		buttons.forEach((el) => {
			el.disabled = false;
		});
		button.removeAttribute("aria-busy");
		button.textContent = label;
		if (errorEl) {
			errorEl.hidden = false;
			errorEl.textContent =
				button.dataset.errorMessage ||
				"We couldn't update shipping protection. Your cart was not changed.";
		}
	};

	if (choice === "decline") {
		element.updateState(false);
		element
			.removeInsurance(false, { render: false })
			.then((cart) => {
				if (protectionVariantStillInCart(cart, element.insuranceVariantId)) {
					throw new Error("Protection is still in the cart");
				}
				const root = Shopify.routes?.root || "/";
				window.location.href = `${root}checkout`;
			})
			.catch(fail);
		return;
	}

	if (choice === "add") {
		element.updateState(true);
		element
			.addInsurance(false, { render: true })
			.then(() => {
				protectionChoiceLocked = false;
			})
			.catch(fail);
	}
});

document.addEventListener("submit", (event) => {
	if (protectionChoiceLocked && event.target.closest("cart-drawer")) {
		event.preventDefault();
	}
});
